/**
 * Editor.js strikethrough wraps the provided `Range`. In lists, users often
 * double-click selecting a whole line (including whitespace/newlines). Wrapping
 * those edges can make Editor.js interpret it as additional (empty) list items.
 *
 * Build a new `Range` that starts at the first non-whitespace text node within
 * the original range and ends at the last non-whitespace text node within the
 * original range.
 */
export function trimRangeEdges(range: Range): Range | null {
  const ancestor = range.commonAncestorContainer;
  const walkerRoot = ancestor.nodeType === 3 ? ancestor.parentNode : ancestor;
  if (!walkerRoot) return null;

  const treeWalker = document.createTreeWalker(
    walkerRoot,
    NodeFilter.SHOW_TEXT,
  );

  const intersectingNonWhitespaceTextNodes: Text[] = [];
  let node = treeWalker.nextNode() as Node | null;
  while (node) {
    const textNode = node as Text;
    if (range.intersectsNode(textNode) && textNode.data?.trim()) {
      intersectingNonWhitespaceTextNodes.push(textNode);
    }
    node = treeWalker.nextNode() as Node | null;
  }

  if (intersectingNonWhitespaceTextNodes.length === 0) return null;

  const first = intersectingNonWhitespaceTextNodes[0];
  const last =
    intersectingNonWhitespaceTextNodes[
      intersectingNonWhitespaceTextNodes.length - 1
    ];

  const firstStartLimit =
    range.startContainer === first ? range.startOffset : 0;
  const lastEndLimit =
    range.endContainer === last ? range.endOffset : last.data.length;

  let startOffset = Math.min(firstStartLimit, first.data.length);
  while (
    startOffset < first.data.length &&
    /\s/.test(first.data[startOffset] ?? '')
  ) {
    startOffset += 1;
  }

  let endOffset = Math.max(0, Math.min(lastEndLimit, last.data.length));
  while (endOffset > startOffset && /\s/.test(last.data[endOffset - 1] ?? '')) {
    endOffset -= 1;
  }

  if (endOffset <= startOffset) return null;

  const trimmed = document.createRange();
  trimmed.setStart(first, startOffset);
  trimmed.setEnd(last, endOffset);

  return trimmed.toString().trim() ? trimmed : null;
}

export function createSafeStrikethroughTool(StrikethroughTool: any): any {
  class SafeStrikethroughTool extends StrikethroughTool {
    surround(range: Range) {
      const trimmed = trimRangeEdges(range);
      if (!trimmed) return;
      return super.surround(trimmed);
    }
  }
  return SafeStrikethroughTool;
}
